import { LogOut, type LucideIcon, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Avatar } from '@/components/admin/avatar'
import { Button } from '@/components/ui/button'
import { signOut } from '@/lib/auth-client'
import type { AdminMe } from '@/lib/admin-api'
import { cn } from '@/utils/misc'

export type NavItem = {
	to: string
	label: string
	icon: LucideIcon
	end?: boolean
}

export function AdminSidebar({
	items,
	user,
}: {
	items: NavItem[]
	user: AdminMe | undefined
}) {
	const [mobileOpen, setMobileOpen] = useState(false)

	return (
		<>
			{/* Mobile top bar */}
			<div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-gray-200 h-14 flex items-center justify-between px-4">
				<img src="/agnis_logo_crop.png" alt="Agnis" className="h-7" />
				<button
					type="button"
					onClick={() => setMobileOpen(true)}
					className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
					aria-label="Open menu"
				>
					<Menu className="h-5 w-5" />
				</button>
			</div>

			{/* Mobile overlay */}
			{mobileOpen && (
				<button
					type="button"
					className="lg:hidden fixed inset-0 z-40 bg-black/30"
					onClick={() => setMobileOpen(false)}
					aria-label="Close menu"
				/>
			)}

			<aside
				className={cn(
					'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col transition-transform lg:translate-x-0',
					mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
				)}
			>
				{/* Logo */}
				<div className="h-16 border-b border-gray-200 flex items-center justify-between px-6">
					<img src="/agnis_logo_crop.png" alt="Agnis" className="h-8" />
					<button
						type="button"
						onClick={() => setMobileOpen(false)}
						className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
						aria-label="Close menu"
					>
						<X className="h-5 w-5" />
					</button>
				</div>

				{/* Nav */}
				<nav className="flex-1 overflow-y-auto px-3 py-4">
					<ul className="space-y-1">
						{items.map((item) => (
							<li key={item.to}>
								<NavLink
									to={item.to}
									end={item.end}
									onClick={() => setMobileOpen(false)}
									className={({ isActive }) =>
										cn(
											'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
											isActive
												? 'bg-red-50 text-red-700'
												: 'text-gray-700 hover:bg-gray-50',
										)
									}
								>
									<item.icon className="h-4 w-4 shrink-0" />
									<span>{item.label}</span>
								</NavLink>
							</li>
						))}
					</ul>
				</nav>

				{/* User card */}
				<UserCard user={user} />
			</aside>
		</>
	)
}

function UserCard({ user }: { user: AdminMe | undefined }) {
	const navigate = useNavigate()
	const [signingOut, setSigningOut] = useState(false)

	const handleSignOut = async () => {
		setSigningOut(true)
		try {
			await signOut()
		} finally {
			navigate('/login', { replace: true })
		}
	}

	return (
		<div className="border-t border-gray-200 p-3">
			<NavLink
				to="/admin/profile"
				className={({ isActive }) =>
					cn(
						'flex items-center gap-3 px-2 py-2 rounded-lg transition-colors',
						isActive
							? 'bg-red-50'
							: 'hover:bg-gray-50',
					)
				}
			>
				<Avatar
					src={user?.image}
					name={user?.name}
					email={user?.email}
					size="sm"
				/>
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-gray-900 truncate">
						{user?.name || user?.email?.split('@')[0] || 'Admin'}
					</p>
					<p className="text-xs text-gray-500 truncate">{user?.email}</p>
				</div>
			</NavLink>
			<Button
				type="button"
				variant="ghost"
				size="sm"
				onClick={handleSignOut}
				disabled={signingOut}
				className="w-full justify-start text-gray-600 hover:text-gray-900 hover:bg-gray-50 mt-1"
			>
				<LogOut className="h-4 w-4 mr-2" />
				{signingOut ? 'Signing out...' : 'Sign out'}
			</Button>
		</div>
	)
}
